import { customerPaymentOptionFriendlyNameMap } from "@portal/app/_common/data/enumValueMaps";
import { renderDollarAmount } from "@portal/app/_common/lib/formatRenderers";
import {
  sumProgramCosts,
  calculateLowestStartAmount,
} from "@portal/app/_common/lib/programCostUtils";
import { DreamSearchViewerResult } from "@portal/app/_common/types/dreamSearch";
import Typography from "@mui/material/Typography";

export const DreamSearchResultPaymentInfo = ({
  item,
}: {
  item: Pick<DreamSearchViewerResult, "program" | "provider">;
}) => {
  // We will show total cost of the class if there are ProgramCosts.

  // Exception 1: If the ONLY enabled PaymentOption is "Financial aid", the
  // total cost of class will not be shown.
  const isFinancialAidOnlyPaymentOption =
    item.program.eligiblePaymentOptions.length === 1 &&
    item.program.eligiblePaymentOptions[0].type === "SCHOOL_FINANCIAL_AID";

  // Exception 2: If "SCHOOL_NON_PLATFORM" is the ONLY payment option visible,
  // show custom description regardless of what costs are present.
  const visiblePaymentOptions = item.program.eligiblePaymentOptions.filter(
    (option) => option.isVisible
  );

  const isNonPlatformOnlyVisiblePaymentOption =
    visiblePaymentOptions?.length === 1 && visiblePaymentOptions[0].type === "SCHOOL_NON_PLATFORM";

  const estimatedFirstPayment = calculateLowestStartAmount(item);
  const estimatedFirstPaymentFormatted =
    estimatedFirstPayment !== null ? renderDollarAmount(estimatedFirstPayment, true, true) : "";
  const totalCost = sumProgramCosts(item.program.costs);
  const hasCosts = item.program.costs.length > 0;
  const totalCostFormatted = hasCosts ? renderDollarAmount(totalCost, true, true) : "";
  const customDescription = item.program.eligiblePaymentOptions.find(
    (option) => option.type === "SCHOOL_NON_PLATFORM"
  )?.config.description;

  if (isNonPlatformOnlyVisiblePaymentOption && customDescription) {
    return (
      <Typography sx={{ fontWeight: "bold" }} data-testid="non-platform-text">
        {customDescription}
      </Typography>
    );
  }

  /**
   * If the only enabled payment option is financial aid, show the financial aid instead of the total cost.
   */
  if (isFinancialAidOnlyPaymentOption) {
    return (
      <Typography variant="disclaimer" data-testid="financial-aid-text">
        {customerPaymentOptionFriendlyNameMap["SCHOOL_FINANCIAL_AID"]}
      </Typography>
    );
  }

  /**
   * If we have an estimated first payment, and it's different from the total cost,
   * show the estimated first payment and the total cost.
   */
  if (estimatedFirstPayment && estimatedFirstPaymentFormatted !== totalCostFormatted) {
    return (
      <>
        <Typography sx={{ fontWeight: "bold" }} data-testid="estimated-first-payment-text">
          {estimatedFirstPaymentFormatted} to start
        </Typography>
        <Typography variant="disclaimer">{totalCostFormatted} total</Typography>
      </>
    );
  }

  if (hasCosts) {
    return (
      <Typography sx={{ fontWeight: "bold" }} data-testid="total-cost-text">
        {totalCostFormatted} total
      </Typography>
    );
  }

  return <Typography sx={{ fontWeight: "bold" }}>No cost info</Typography>;
};
